/**
 * Admin Vendor Roster (Iter 67eh).
 *
 * Live directory of every vendor account with full business contact
 * details (responsible person, address, business name, email, WhatsApp,
 * phone, VAT, country) plus fleet/booking activity counts. Sits inside
 * the Admin Ops panel under a new "Providers" tab and mirrors the
 * customer roster so admins have one mental model for both rosters.
 *
 * Two extras vs. the customer roster:
 *   1. "+ Add vendor manually" dialog — admin can pre-create a vendor
 *      account for an off-platform partner. Backend returns a one-time
 *      temp password that the admin then shares via WhatsApp / encrypted
 *      email / in person.
 *   2. Vendor-specific columns (business name, VAT, car count).
 *
 * Pairs with the broadcast notifications hand-off the same way the
 * customer roster does: ticking rows + clicking "Email selected" pushes
 * `ids:uuid1,uuid2,…` into the Notifications tab audience.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { impersonate } from "@/components/AdminImpersonationBanner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Download, Search, Send, Loader2, Building2, Mail, Phone, MapPin,
  Globe, RefreshCw, Plus, Car as CarIcon, Copy, Check, Eye, EyeOff, Pencil, X as XIcon, KeyRound, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";


/**
 * Iter 74r — small row-level button that triggers a password-reset email
 * for a vendor via `POST /admin/vendors/{id}/reset-password`. Two clicks
 * gate the destructive action (first click flips to "Confirm?" for 3s
 * before firing, so an accidental tap doesn't spam the vendor's inbox).
 */
function ResetPasswordButton({ vendor }) {
  const [state, setState] = useState("idle"); // "idle" | "confirm" | "sending"

  const trigger = async () => {
    if (state === "idle") {
      setState("confirm");
      window.setTimeout(() => {
        setState((s) => (s === "confirm" ? "idle" : s));
      }, 3000);
      return;
    }
    if (state !== "confirm") return;
    setState("sending");
    try {
      const { data } = await api.post(`/admin/vendors/${vendor.id}/reset-password`);
      if (data?.email_sent) {
        toast.success(`Reset link emailed to ${vendor.email}`);
      } else {
        toast.warning("Token generated but email dispatch failed. Check backend logs.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setState("idle");
    }
  };

  const label = state === "confirm" ? "Confirm?" : state === "sending" ? "Sending" : "Reset";
  const cls = state === "confirm"
    ? "border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100"
    : state === "sending"
      ? "border-neutral-200 text-neutral-400 cursor-wait"
      : "border-border text-neutral-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800";
  return (
    <button
      type="button"
      onClick={trigger}
      disabled={state === "sending" || !vendor?.email}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border transition ${cls} ${!vendor?.email ? "opacity-40 cursor-not-allowed" : ""}`}
      title={vendor?.email ? `Email a password reset link to ${vendor.email}` : "Vendor has no email on file"}
      data-testid={`vendor-reset-pw-${vendor.id}`}
    >
      <KeyRound className="h-3 w-3" />
      {label}
    </button>
  );
}


export default function AdminVendorRoster({ onBroadcastSelected }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [credentialSheet, setCredentialSheet] = useState(null); // {email, temp_password}
  // Iter 74p — inline email editing: which row is currently in edit mode,
  // the pending new email string, and a saving flag to gate the button.
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [emailDraft, setEmailDraft] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const startEmailEdit = (row) => {
    setEditingEmailId(row.id);
    setEmailDraft(row.email || "");
  };
  const cancelEmailEdit = () => {
    setEditingEmailId(null);
    setEmailDraft("");
  };
  const saveEmail = async (row) => {
    const newEmail = emailDraft.trim().toLowerCase();
    if (!newEmail || newEmail === (row.email || "").toLowerCase()) {
      cancelEmailEdit();
      return;
    }
    setSavingEmail(true);
    try {
      const { data } = await api.patch(`/admin/vendors/${row.id}/email`, { email: newEmail });
      // Optimistic row update so the roster reflects immediately without a
      // full reload — avoids losing scroll position.
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, email: data.email } : r)));
      toast.success("Vendor email updated");
      cancelEmailEdit();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEmail(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/vendors/roster`, {
        params: { search, limit: 1000 },
      });
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/vendors/roster.csv?search=${encodeURIComponent(search)}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1]
        || `rentalcar-vendor-roster.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch {
      toast.error("Could not download CSV. Try again or use a different browser.");
    }
  };

  const summary = useMemo(() => ({
    total: rows.length,
    approved: rows.filter((r) => r.vendor_status === "approved").length,
    pending: rows.filter((r) => r.vendor_status === "pending").length,
    withCars: rows.filter((r) => (r.car_count || 0) > 0).length,
  }), [rows]);

  return (
    <div className="space-y-4" data-testid="admin-vendor-roster">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-display text-xl font-medium tracking-tight flex items-center gap-2">
            <Building2 className="h-4 w-4 text-emerald-600" />
            Provider directory
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            Full business contact details for every vendor account · use for partnership
            outreach, account management, and broadcasting platform updates. Address &amp; phone
            fall back across personal + business fields so you always see the most complete data.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          className="rounded-full"
          data-testid="vendor-roster-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
        <Button
          type="button"
          onClick={() => setAddOpen(true)}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700"
          data-testid="vendor-roster-add"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add vendor manually
        </Button>
        <Button
          type="button"
          onClick={downloadCsv}
          variant="outline"
          className="rounded-full"
          data-testid="vendor-roster-csv"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          Download CSV
        </Button>
      </header>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="vendor-roster-summary">
        <_Stat label="Providers"  value={summary.total}    icon={Building2} tone="emerald" />
        <_Stat label="Approved"   value={summary.approved} icon={Check}     tone="blue" />
        <_Stat label="Pending"    value={summary.pending}  icon={Mail}      tone="amber" />
        <_Stat label="With cars"  value={summary.withCars} icon={CarIcon}   tone="violet" />
      </div>

      {/* Search + bulk-action bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, business, email, phone, VAT, address…"
            className="pl-8 rounded-full h-9"
            data-testid="vendor-roster-search"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onBroadcastSelected?.([...selected])}
          disabled={selected.size === 0 || !onBroadcastSelected}
          className="rounded-full"
          data-testid="vendor-roster-broadcast"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Email {selected.size > 0 ? `${selected.size} selected` : "selected"}
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-border overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === rows.length}
                    onChange={toggleAll}
                    aria-label="Select all"
                    className="h-3.5 w-3.5"
                    data-testid="vendor-roster-select-all"
                  />
                </th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Business</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left flex items-center gap-1"><Phone className="h-3 w-3" /> WhatsApp / phone</th>
                <th className="px-3 py-2 text-left flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</th>
                <th className="px-3 py-2 text-left"><Globe className="h-3 w-3 inline" /></th>
                <th className="px-3 py-2 text-left">Cars</th>
                <th className="px-3 py-2 text-left">Bookings</th>
                <th className="px-3 py-2 text-left">Approval</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan="12" className="px-3 py-6 text-center text-neutral-400">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="12" className="px-3 py-6 text-center text-neutral-500">No vendors match this search.</td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={`border-t border-border ${selected.has(r.id) ? "bg-emerald-50/50" : "hover:bg-neutral-50/60"}`}
                  data-testid={`vendor-roster-row-${r.id}`}
                >
                  <td className="px-3 py-2 align-top">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                      aria-label={`Select ${r.full_name}`}
                      className="h-3.5 w-3.5"
                      data-testid={`vendor-roster-select-${r.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-neutral-900">{r.full_name || "—"}</td>
                  <td className="px-3 py-2 text-neutral-800">
                    <div className="truncate max-w-[180px]" title={r.business_name || ""}>{r.business_name || <span className="text-neutral-400">—</span>}</div>
                    {r.vat_number && <div className="text-[10px] font-mono text-neutral-500 mt-0.5">VAT: {r.vat_number}</div>}
                  </td>
                  <td className="px-3 py-2">
                    {editingEmailId === r.id ? (
                      <div className="flex items-center gap-1" data-testid={`vendor-email-edit-${r.id}`}>
                        <Input
                          type="email"
                          value={emailDraft}
                          onChange={(e) => setEmailDraft(e.target.value)}
                          className="h-7 text-xs w-52"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEmail(r);
                            if (e.key === "Escape") cancelEmailEdit();
                          }}
                          data-testid={`vendor-email-input-${r.id}`}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          onClick={() => saveEmail(r)}
                          disabled={savingEmail}
                          title="Save"
                          data-testid={`vendor-email-save-${r.id}`}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-neutral-500 hover:bg-neutral-50"
                          onClick={cancelEmailEdit}
                          disabled={savingEmail}
                          title="Cancel"
                          data-testid={`vendor-email-cancel-${r.id}`}
                        >
                          <XIcon className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 group">
                        <a href={`mailto:${r.email}`} className="text-blue-700 hover:underline" data-testid={`vendor-email-${r.id}`}>{r.email}</a>
                        <button
                          type="button"
                          onClick={() => startEmailEdit(r)}
                          className="text-neutral-400 hover:text-neutral-700 opacity-0 group-hover:opacity-100 transition"
                          title="Edit email"
                          data-testid={`vendor-email-edit-btn-${r.id}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    {r.business_email && r.business_email !== r.email && (
                      <div className="text-[10px] text-neutral-500 mt-0.5 truncate max-w-[180px]" title={r.business_email}>biz: {r.business_email}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.whatsapp ? (
                      <a
                        href={`https://wa.me/${r.whatsapp.replace(/[^+\d]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                        data-testid={`vendor-whatsapp-${r.id}`}
                      >
                        {r.whatsapp}
                      </a>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-2 max-w-[260px] truncate text-neutral-700" title={r.address || ""}>
                    {r.address || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-2 uppercase tracking-wider text-neutral-500">{r.country || "—"}</td>
                  <td className="px-3 py-2">
                    {r.car_count > 0
                      ? <Badge variant="secondary" className="text-[10px]">{r.car_count}</Badge>
                      : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {r.booking_count > 0
                      ? <Badge variant="secondary" className="text-[10px]">{r.booking_count}</Badge>
                      : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <_ApprovalCell vendor={r} />
                  </td>
                  <td className="px-3 py-2">
                    <_StatusPill status={r.vendor_status} active={r.is_active} />
                  </td>
                  {/* Iter 74r — row-level actions column. Password reset
                      button sends a Resend email with a 1h token via
                      POST /admin/vendors/{id}/reset-password. Optimistic
                      loading state; toast on success/error. */}
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => impersonate(r.id, "vendor").catch(() => toast.error("Could not open vendor dashboard"))}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-border text-neutral-600 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 transition"
                      title="Enter this vendor's dashboard as admin (full access, audited)"
                      data-testid={`vendor-open-dashboard-${r.id}`}
                    >
                      <ExternalLink className="h-3 w-3" /> Open Dashboard
                    </button>
                    <ResetPasswordButton vendor={r} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[10px] text-neutral-400 leading-relaxed">
        Vendor contact data is treated as confidential business information.
        Manually-added accounts receive a one-time temp password the admin
        must share via a secure channel — the platform never re-displays it.
      </p>

      <ManualAddVendorDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(payload) => {
          setCredentialSheet(payload);
          setAddOpen(false);
          load();
        }}
      />

      <CredentialSheetDialog
        sheet={credentialSheet}
        onClose={() => setCredentialSheet(null)}
      />
    </div>
  );
}

function _Stat({ label, value, icon: Icon, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    blue:    "bg-blue-50 text-blue-700 ring-blue-200",
    violet:  "bg-violet-50 text-violet-700 ring-violet-200",
    amber:   "bg-amber-50 text-amber-700 ring-amber-200",
  };
  return (
    <div className={`rounded-xl ring-1 px-3 py-2 ${tones[tone] || tones.blue}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-display font-medium leading-tight mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

function _StatusPill({ status, active }) {
  if (!active) {
    return <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600 border border-neutral-200 text-[10px] font-semibold">Disabled</span>;
  }
  if (status === "approved") {
    return <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold">Approved</span>;
  }
  if (status === "pending") {
    return <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold">Pending</span>;
  }
  if (status === "rejected") {
    return <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-semibold">Rejected</span>;
  }
  return <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600 border border-neutral-200 text-[10px] font-semibold">{status || "—"}</span>;
}

function ManualAddVendorDialog({ open, onOpenChange, onCreated }) {
  const empty = {
    email: "", name: "", business_name: "", responsible_person: "",
    business_address: "", phone: "", whatsapp: "", vat_number: "",
    country: "", auto_approve: true,
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (!open) setForm(empty); /* eslint-disable-next-line */ }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email.trim() || !form.name.trim()) {
      toast.error("Email and full name are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        country: form.country ? form.country.toUpperCase() : null,
      };
      // Drop empty strings so backend sees None and doesn't reject EmailStr.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      const { data } = await api.post("/admin/vendors", payload);
      toast.success(`Vendor "${data.vendor.name}" created`);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="vendor-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">Add vendor manually</DialogTitle>
          <DialogDescription>
            Pre-create a vendor account for an off-platform partner. You'll receive a one-time
            temp password to share with them — they can change it after their first login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Full name *</Label>
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} className="rounded-xl mt-1" required data-testid="vendor-add-name" />
          </div>
          <div>
            <Label className="text-xs">Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="rounded-xl mt-1" required data-testid="vendor-add-email" />
          </div>
          <div>
            <Label className="text-xs">Business email</Label>
            <Input type="email" value={form.business_email || ""} onChange={(e) => set("business_email")(e.target.value)} className="rounded-xl mt-1" data-testid="vendor-add-business-email" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Business name / Company</Label>
            <Input value={form.business_name} onChange={(e) => set("business_name")(e.target.value)} className="rounded-xl mt-1" data-testid="vendor-add-business" />
          </div>
          <div>
            <Label className="text-xs">Responsible person</Label>
            <Input value={form.responsible_person} onChange={(e) => set("responsible_person")(e.target.value)} className="rounded-xl mt-1" data-testid="vendor-add-responsible" />
          </div>
          <div>
            <Label className="text-xs">VAT / NIPT</Label>
            <Input value={form.vat_number} onChange={(e) => set("vat_number")(e.target.value)} className="rounded-xl mt-1 font-mono" data-testid="vendor-add-vat" />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input value={form.phone} onChange={(e) => set("phone")(e.target.value)} className="rounded-xl mt-1" placeholder="+33 6 12 34 56 78" data-testid="vendor-add-phone" />
          </div>
          <div>
            <Label className="text-xs">WhatsApp</Label>
            <Input value={form.whatsapp} onChange={(e) => set("whatsapp")(e.target.value)} className="rounded-xl mt-1" placeholder="+33 6 12 34 56 78" data-testid="vendor-add-whatsapp" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Business address</Label>
            <Input value={form.business_address} onChange={(e) => set("business_address")(e.target.value)} className="rounded-xl mt-1" placeholder="Street, city, postcode" data-testid="vendor-add-address" />
          </div>
          <div>
            <Label className="text-xs">Country (ISO-2)</Label>
            <Input value={form.country} onChange={(e) => set("country")(e.target.value.toUpperCase().slice(0, 2))} className="rounded-xl mt-1 uppercase tracking-wider font-mono" placeholder="FR" data-testid="vendor-add-country" />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 cursor-pointer text-xs select-none mt-1" data-testid="vendor-add-auto-approve-label">
              <input
                type="checkbox"
                checked={form.auto_approve}
                onChange={(e) => set("auto_approve")(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-emerald-400 text-emerald-600 focus:ring-emerald-500"
                data-testid="vendor-add-auto-approve"
              />
              <span>
                <strong>Auto-approve</strong> — vendor can list cars immediately. Uncheck to put them in the pending-approval queue first.
              </span>
            </label>
          </div>
          <DialogFooter className="md:col-span-2 mt-2 flex flex-row justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="vendor-add-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="vendor-add-submit">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
              Create vendor
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CredentialSheetDialog({ sheet, onClose }) {
  const [copied, setCopied] = useState(false);
  const [show, setShow] = useState(true);
  if (!sheet) return null;
  const { vendor, temp_password } = sheet;
  const copy = async () => {
    // Robust clipboard copy — `navigator.clipboard` is unavailable in
    // insecure contexts (http:// previews, sandboxed iframes), and even
    // when present a permissions denial throws NotAllowedError. We fall
    // back to a hidden-textarea + execCommand('copy') so the action
    // never bubbles an unhandled rejection up to React's error overlay.
    const text =
      `RentalCar.al — new vendor account\nEmail: ${vendor.email}\n` +
      `Temp password: ${temp_password}\nLogin: ${window.location.origin}/login`;
    let ok = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch { /* fall through to textarea fallback */ }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        ta.remove();
      } catch { ok = false; }
    }
    if (ok) {
      setCopied(true);
      toast.success("Credentials copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error("Couldn't access the clipboard — please copy the values manually.");
    }
  };
  return (
    <Dialog open={!!sheet} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="vendor-credentials-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">Vendor credentials</DialogTitle>
          <DialogDescription className="text-xs">
            Share these with <strong>{vendor.name}</strong> via a secure channel (WhatsApp,
            encrypted email, in person). This is the only time the password will be shown.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Email</div>
            <div className="text-sm font-mono mt-0.5" data-testid="vendor-credentials-email">{vendor.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 flex items-center justify-between">
              <span>Temp password</span>
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 text-[10px]"
                data-testid="vendor-credentials-toggle"
              >
                {show ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {show ? "Hide" : "Show"}
              </button>
            </div>
            <div className="text-sm font-mono mt-0.5 select-all" data-testid="vendor-credentials-password">
              {show ? temp_password : "•".repeat(temp_password.length)}
            </div>
          </div>
        </div>
        <DialogFooter className="flex flex-row justify-end gap-2 mt-2">
          <Button type="button" onClick={copy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="vendor-credentials-copy">
            {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
            {copied ? "Copied" : "Copy login details"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full" data-testid="vendor-credentials-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Iter 90 — inline per-vendor host-approval controls (toggle + deadline). */
function _ApprovalCell({ vendor }) {
  const [requireApproval, setRequireApproval] = useState(vendor.require_approval !== false);
  const [hours, setHours] = useState(vendor.approval_deadline_hours || 2);
  const [draftHours, setDraftHours] = useState(String(vendor.approval_deadline_hours || 2));
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const h = Math.max(0.25, Math.min(72, parseFloat(draftHours) || 2));
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/vendors/${vendor.id}/approval-settings`, {
        require_approval: requireApproval,
        approval_deadline_hours: h,
      });
      setRequireApproval(data.require_approval);
      setHours(data.approval_deadline_hours);
      setDraftHours(String(data.approval_deadline_hours));
      toast.success("Approval settings saved");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const stats = vendor.approval_stats || { total: 0, avg_response_minutes: null, expiry_rate_pct: 0, expired: 0 };
  const fmtMins = (m) => (m == null ? "—" : m < 60 ? `${Math.round(m)}m` : `${(m / 60).toFixed(1)}h`);
  const slow = stats.expiry_rate_pct >= 20 || (stats.avg_response_minutes != null && stats.avg_response_minutes >= hours * 45);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-left"
          title="Host approval settings & responsiveness — click to edit"
          data-testid={`vendor-approval-cell-${vendor.id}`}
        >
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border transition ${
              requireApproval
                ? "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"
                : "bg-neutral-100 text-neutral-500 border-neutral-200 hover:bg-neutral-200"
            }`}
          >
            {requireApproval ? `ON · ${hours}h` : "Instant"}
          </span>
          {stats.total > 0 && (
            <span
              className={`block mt-0.5 text-[10px] tabular-nums ${slow ? "text-rose-600 font-semibold" : "text-neutral-400"}`}
              data-testid={`vendor-approval-stats-${vendor.id}`}
            >
              ⌀ {fmtMins(stats.avg_response_minutes)} · {stats.expiry_rate_pct}% exp
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="start" data-testid={`vendor-approval-popover-${vendor.id}`}>
        <div className="space-y-3">
          <div className="text-xs font-semibold">Host approval — {vendor.business_name || vendor.full_name}</div>
          <div className="rounded-lg bg-neutral-50 border border-border p-2 text-[11px] space-y-0.5" data-testid={`vendor-approval-stats-detail-${vendor.id}`}>
            <div className="flex justify-between"><span className="text-neutral-500">Approval requests</span><span className="font-semibold tabular-nums">{stats.total}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Avg response time</span><span className="font-semibold tabular-nums">{fmtMins(stats.avg_response_minutes)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-500">Expired (no response)</span><span className={`font-semibold tabular-nums ${stats.expiry_rate_pct >= 20 ? "text-rose-600" : ""}`}>{stats.expired} · {stats.expiry_rate_pct}%</span></div>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Switch
              checked={requireApproval}
              onCheckedChange={setRequireApproval}
              className="data-[state=checked]:bg-amber-500"
              data-testid={`vendor-approval-require-${vendor.id}`}
            />
            Require approval before confirmation
          </label>
          <div>
            <Label className="text-xs">Response deadline (hours)</Label>
            <Input
              type="number"
              min="0.25"
              max="72"
              step="0.25"
              value={draftHours}
              onChange={(e) => setDraftHours(e.target.value)}
              disabled={!requireApproval}
              className="h-8 mt-1 text-xs"
              data-testid={`vendor-approval-hours-${vendor.id}`}
            />
            <p className="text-[10px] text-neutral-500 mt-1">Unanswered requests expire &amp; auto-refund after this window.</p>
          </div>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="w-full rounded-full bg-amber-600 hover:bg-amber-700"
            data-testid={`vendor-approval-save-${vendor.id}`}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
